import { useEffect, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarBlank,
  CheckCircle,
  SidebarSimple,
  Eye,
  EyeSlash,
  DotsThreeVertical,
  DownloadSimple,
  FileText,
  Keyboard,
  Gear,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useHomeworkStore } from "../../stores/homework";
import { useDocumentStore } from "../../stores/document";
import { useUIStore } from "../../stores/ui";
import { useAIStore } from "../../stores/ai";
import { useEditorStore } from "../../stores/editor";
import { useChatStore } from "../../stores/chat";
import { useEquationPlaygroundStore } from "../../stores/equationPlayground";
import { useCollaborationStore } from "../../stores/collaboration";
import { useInlineAssistantStore } from "../../stores/inlineAssistant";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useOfflineSync } from "../../hooks/useOfflineSync";
import { OfflineBanner } from "../../components/ui/OfflineBanner";
import { useToast } from "../../components/ui/Toast";
import { useContextStore } from "../../stores/context";
import { useVitalsStore } from "../../stores/vitals";
import { LeverEditor } from "../../components/editor/LeverEditor";
import { EditorToolbar } from "../../components/editor/EditorToolbar";
import { StatusBar } from "../../components/editor/StatusBar";
import { SelectionToolbar } from "../../components/editor/SelectionToolbar";
import { AIInlineSuggestion } from "../ai/AIInlineSuggestion";
import { InlineAssistant } from "../ai/InlineAssistant";
import { EquationPreview } from "../ai/EquationPreview";
import { DiffOverlay } from "../../components/editor/DiffOverlay";
import { PreviewPanel } from "../../components/preview/PreviewPanel";
import { ChatPanel } from "../../components/homework/ChatPanel";
import { BreadcrumbBar } from "../../components/navigation/BreadcrumbBar";
import { AmbientBackground } from "../../components/layout/AmbientBackground";
import { Sidebar } from "../../components/layout/Sidebar";
import { BottomPanel } from "../../components/layout/BottomPanel";
import { SplitPane } from "../../components/layout/SplitPane";
import { OutlineTree } from "../../components/navigation/OutlineTree";
import { FileTree } from "../document/FileTree";
import { CommentPopover } from "../../components/collaboration/CommentPopover";
import { CommentThread } from "../../components/collaboration/CommentThread";
import { CitationPanel } from "../../components/citations/CitationPanel";
import { ContextPanel } from "../../components/context/ContextPanel";
import { EquationPlayground } from "../../components/equation/EquationPlayground";
import { VitalsDashboard } from "../../components/vitals/VitalsDashboard";
import { VersionPanel } from "../version-control/VersionPanel";
import { ErrorList } from "../document/ErrorList";
import { ModelSelector } from "../../components/ai/ModelSelector";
import { AgentStatusIndicator } from "../../components/ai/AgentStatus";
import { KeyboardShortcutsDialog } from "../../components/editor/KeyboardShortcutsDialog";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSeparator,
} from "../../components/ui/Dropdown";
import { springDefault } from "../../lib/motion-config";

const spring = springDefault;

export function AssignmentWorkspace() {
  const { classId, assignmentId } = useParams<{
    classId: string;
    assignmentId: string;
  }>();
  const navigate = useNavigate();

  const {
    currentCourse,
    currentAssignment,
    fetchCourse,
    fetchAssignment,
    completeAssignment,
    reopenAssignment,
  } = useHomeworkStore();

  const {
    currentDocument,
    documents,
    errors,
    unsavedChanges,
    compileResult,
    pendingEdit,
    fetchDocuments,
    openDocument,
    updateContent,
    saveDocument,
    compile,
    proposePendingEdit,
    acceptPendingEdit,
    rejectPendingEdit,
  } = useDocumentStore();

  const focusMode = useUIStore((s) => s.focusMode);
  const toggleFocusMode = useUIStore((s) => s.toggleFocusMode);
  const editTextStream = useAIStore((s) => s.editTextStream);
  const explainText = useAIStore((s) => s.explainText);
  const cursorLine = useEditorStore((s) => s.cursorLine);
  const selection = useEditorStore((s) => s.selection);
  const editorView = useEditorStore((s) => s.editorView);

  const sendMessageStream = useChatStore((s) => s.sendMessageStream);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const setSidebarTab = useUIStore((s) => s.setSidebarTab);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const buildContext = useContextStore((s) => s.buildContext);
  const toggleInlineAssistant = useInlineAssistantStore((s) => s.toggle);
  const inlineAssistantOpen = useInlineAssistantStore((s) => s.isOpen);
  const openPlayground = useEquationPlaygroundStore((s) => s.open);
  const openCommentPopover = useCollaborationStore((s) => s.openCommentPopover);
  const setShowSelectionToolbar = useEditorStore((s) => s.setShowSelectionToolbar);
  const selectionToolbarPosition = useEditorStore((s) => s.selectionToolbarPosition);
  const comments = useCollaborationStore((s) => s.comments);
  const fetchComments = useCollaborationStore((s) => s.fetchComments);
  const addComment = useCollaborationStore((s) => s.addComment);
  const resolveComment = useCollaborationStore((s) => s.resolveComment);
  const deleteComment = useCollaborationStore((s) => s.deleteComment);
  const vitalsAnalyze = useVitalsStore((s) => s.analyze);

  useDocumentTitle(currentAssignment?.title ?? "Assignment");
  useAutoSave();
  const { isOnline, isSyncing, pendingCount } = useOfflineSync();
  const { error: toastError, success: toastSuccess, ai: toastAI } = useToast();

  useEffect(() => {
    return useDocumentStore.getState().onSaveEvent((event) => {
      if (event === "save_failed") {
        toastError("Save failed", "Your changes are not saved. Check your connection.");
      } else if (event === "save_recovered") {
        toastSuccess("Saved", "Connection restored, document saved.");
      }
    });
  }, [toastError, toastSuccess]);

  const [completeError, setCompleteError] = useState<string | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const handleDownloadSource = useCallback(() => {
    if (!currentDocument) return;
    const blob = new Blob([currentDocument.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = currentDocument.filename || "document.tex";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentDocument]);

  const handleDownloadPdf = useCallback(() => {
    const pdfUrl = useDocumentStore.getState().compileResult?.pdf_url;
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  useEffect(() => {
    if (classId) fetchCourse(classId);
    if (assignmentId) fetchAssignment(assignmentId);
  }, [classId, assignmentId, fetchCourse, fetchAssignment]);

  useEffect(() => {
    if (assignmentId) buildContext(assignmentId);
  }, [assignmentId, buildContext]);

  useEffect(() => {
    if (currentAssignment && sidebarOpen) {
      setSidebarTab("context");
    }
    // Only run on initial assignment load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssignment?.id]);

  useEffect(() => {
    if (currentAssignment?.project_id) {
      fetchDocuments(currentAssignment.project_id);
    }
  }, [currentAssignment?.project_id, fetchDocuments]);

  useEffect(() => {
    if (currentAssignment?.project_id && documents.length > 0 && !currentDocument) {
      openDocument(documents[0].id);
    }
  }, [currentAssignment?.project_id, documents, currentDocument, openDocument]);

  useEffect(() => {
    if (currentDocument?.id) fetchComments(currentDocument.id);
  }, [currentDocument?.id, fetchComments]);

  useEffect(() => {
    if (currentDocument?.content) {
      vitalsAnalyze(currentDocument.content, currentDocument.doc_type || "latex");
    }
  }, [currentDocument?.content, currentDocument?.doc_type, vitalsAnalyze]);

  useEffect(() => {
    const handleInlineAssistantShortcut = (e: KeyboardEvent) => {
      const mod = navigator.platform.toUpperCase().includes("MAC")
        ? e.metaKey
        : e.ctrlKey;
      if (mod && e.key === "g") {
        e.preventDefault();
        e.stopPropagation();
        toggleInlineAssistant();
      }
    };
    window.addEventListener("keydown", handleInlineAssistantShortcut, {
      capture: true,
    });
    return () =>
      window.removeEventListener("keydown", handleInlineAssistantShortcut, {
        capture: true,
      });
  }, [toggleInlineAssistant]);

  const handleCompile = useCallback(() => {
    saveDocument()
      .then(() => compile())
      .catch((err) => {
        if (err instanceof Error) {
          toastError("Compile failed", err.message.slice(0, 80));
        }
      });
  }, [saveDocument, compile, toastError]);

  useKeyboardShortcuts({
    save: handleCompile,
    compile: handleCompile,
  });

  const handleContentChange = useCallback(
    (content: string) => updateContent(content),
    [updateContent],
  );

  const handleEdit = useCallback(
    (edit: { full_new_content?: string; type?: string; newContent?: string; new_content?: string }) => {
      const proposed = edit.full_new_content || edit.newContent || edit.new_content;
      if (proposed) {
        proposePendingEdit(proposed, "AI suggested fix");
      }
    },
    [proposePendingEdit],
  );

  const handleSelectionAction = useCallback(
    async (actionId: string, selectedText: string) => {
      if (!currentDocument) return;
      if (actionId === "comment") {
        if (!editorView || !selectionToolbarPosition) return;
        const sel = selection;
        if (!sel) return;
        const lineStart = editorView.state.doc.lineAt(sel.from).number;
        const lineEnd = editorView.state.doc.lineAt(sel.to).number;
        openCommentPopover({ position: selectionToolbarPosition, lineStart, lineEnd });
        setShowSelectionToolbar(false);
        return;
      }
      if (actionId === "playground") {
        openPlayground(selectedText);
        return;
      }

      const textIndex = currentDocument.content.indexOf(selectedText);
      const contextStart = Math.max(0, textIndex - 200);
      const contextEnd = Math.min(currentDocument.content.length, textIndex + selectedText.length + 200);
      const context = currentDocument.content.substring(contextStart, contextEnd);

      if (actionId === "explain") {
        try {
          const resp = await explainText(selectedText, context);
          toastAI("Explanation", resp.result.slice(0, 120) + (resp.result.length > 120 ? "..." : ""));
        } catch {
          toastError("Explain failed", "Check your AI provider configuration.");
        }
        return;
      }

      const validActions = ["rewrite", "simplify", "expand", "fix", "proofread", "solve", "prove", "hint", "cite", "outline"] as const;
      type ValidAction = (typeof validActions)[number];
      const action = validActions.includes(actionId as ValidAction) ? (actionId as ValidAction) : "rewrite";
      try {
        await editTextStream(selectedText, action, context, currentDocument.doc_type);
        toastAI("Suggestion ready", `${action.charAt(0).toUpperCase() + action.slice(1)} complete`);
      } catch {
        toastError("AI action failed", "Check your AI provider configuration.");
      }
    },
    [currentDocument, editTextStream, explainText, editorView, selection, selectionToolbarPosition, openCommentPopover, setShowSelectionToolbar, openPlayground, toastAI, toastError],
  );

  const scrollToLine = useCallback(
    (line: number) => {
      if (!editorView) return;
      const clampedLine = Math.max(1, Math.min(line, editorView.state.doc.lines));
      const lineInfo = editorView.state.doc.line(clampedLine);
      editorView.dispatch({ selection: { anchor: lineInfo.from }, scrollIntoView: true });
      editorView.focus();
    },
    [editorView],
  );

  const handleComplete = useCallback(async () => {
    if (!assignmentId) return;
    setCompleteError(null);
    try {
      await completeAssignment(assignmentId);
    } catch {
      setCompleteError("Failed to mark complete");
    }
  }, [assignmentId, completeAssignment]);

  const handleReopen = useCallback(async () => {
    if (!assignmentId) return;
    setCompleteError(null);
    try {
      await reopenAssignment(assignmentId);
    } catch {
      setCompleteError("Failed to reopen");
    }
  }, [assignmentId, reopenAssignment]);

  const handleAIFix = useCallback(
    (errorSummary: string) => {
      if (!assignmentId) return;
      sendMessageStream({
        assignmentId,
        content: errorSummary,
        cursorLine,
        selectionStart: selection?.from,
        selectionEnd: selection?.to,
        selectedText: selection?.text,
        onEdit: handleEdit,
      });
    },
    [assignmentId, sendMessageStream, cursorLine, selection, handleEdit],
  );

  const handleCommentFromPopover = useCallback(
    (content: string, lineStart: number, lineEnd: number) => {
      const now = new Date().toISOString();
      addComment({
        id: crypto.randomUUID(),
        document_id: currentDocument?.id || "",
        user_id: "local",
        content,
        line_start: lineStart,
        line_end: lineEnd,
        parent_id: null,
        resolved: false,
        created_at: now,
        updated_at: now,
      });
    },
    [addComment, currentDocument?.id],
  );

  const handleReply = useCallback(
    (content: string, parentId: string) => {
      const parent = comments.find((c) => c.id === parentId);
      const now = new Date().toISOString();
      addComment({
        id: crypto.randomUUID(),
        document_id: currentDocument?.id || "",
        user_id: "local",
        content,
        line_start: parent?.line_start ?? 1,
        line_end: parent?.line_end ?? 1,
        parent_id: parentId,
        resolved: false,
        created_at: now,
        updated_at: now,
      });
    },
    [addComment, currentDocument?.id, comments],
  );

  const dueDate = currentAssignment?.due_date
    ? new Date(currentAssignment.due_date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-[var(--bg-primary)]">
      <AmbientBackground />

      {/* Assignment context bar */}
      <div className="flex shrink-0 items-center gap-3 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]/80 px-3 py-1.5 backdrop-blur-sm z-10">
        <button
          onClick={() => navigate(`/classes/${classId}`)}
          className={cn(
            "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
            "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
            "transition-colors duration-75",
          )}
        >
          <ArrowLeft size={14} />
        </button>

        <button
          onClick={toggleSidebar}
          className={cn(
            "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
            "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
            "transition-colors duration-75",
          )}
        >
          <SidebarSimple size={14} />
        </button>

        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-[var(--text-muted)]">
            {currentCourse?.name}
          </span>
          <span className="text-[var(--text-muted)] opacity-40">/</span>
          <span className="font-medium text-[var(--text-primary)]">
            {currentAssignment?.title}
          </span>
        </div>

        <AgentStatusIndicator className="ml-2" />

        <div className="ml-auto flex items-center gap-2">
          {dueDate && (
            <span className="flex items-center gap-1 text-[11px] text-[var(--text-muted)]">
              <CalendarBlank size={11} />
              Due {dueDate}
            </span>
          )}

          <span
            className={cn(
              "text-[10px] font-medium",
              unsavedChanges
                ? "text-[var(--color-warm-amber)]"
                : "text-[var(--text-muted)]",
            )}
          >
            {unsavedChanges ? "Unsaved" : "Saved"}
          </span>

          {completeError && (
            <span className="text-[10px] text-[var(--color-soft-red)]">{completeError}</span>
          )}

          {currentAssignment?.status === "in_progress" && (
            <button
              onClick={handleComplete}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1 text-[11px] font-medium",
                "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]",
                "hover:bg-[var(--color-signal-green)]/20 transition-colors duration-75",
              )}
            >
              <CheckCircle size={13} />
              Mark Complete
            </button>
          )}

          {currentAssignment?.status === "completed" && (
            <button
              onClick={handleReopen}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1 text-[11px] font-medium",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "border border-[var(--border-hairline)] hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-75",
              )}
            >
              Reopen
            </button>
          )}

          <div className="mx-0.5 h-4 w-px bg-[var(--border-hairline)]" />

          <ModelSelector />

          <div className="mx-0.5 h-4 w-px bg-[var(--border-hairline)]" />

          <button
            onClick={toggleFocusMode}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "transition-colors duration-75",
              focusMode
                ? "text-[var(--color-lever-orange)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
            )}
            title="Toggle focus mode"
          >
            {focusMode ? <EyeSlash size={14} /> : <Eye size={14} />}
          </button>

          <Dropdown>
            <DropdownTrigger
              className={cn(
                "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
            >
              <DotsThreeVertical size={15} weight="bold" />
            </DropdownTrigger>
            <DropdownMenu align="end" className="w-[200px]">
              <DropdownItem icon={<FileText size={14} />} onClick={handleDownloadSource}>
                Download Source
              </DropdownItem>
              <DropdownItem icon={<DownloadSimple size={14} />} onClick={handleDownloadPdf}>
                Download PDF
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem icon={<Keyboard size={14} />} onClick={() => setShortcutsOpen(true)}>
                Keyboard Shortcuts
              </DropdownItem>
              <DropdownItem icon={<Gear size={14} />} onClick={() => navigate("/settings")}>
                Settings
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      {/* Main content: sidebar | editor | preview */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar with Outline + Chat */}
        <motion.div
          className="h-full shrink-0"
          animate={{ opacity: focusMode ? 0.15 : 1 }}
          transition={spring}
          style={{ pointerEvents: focusMode ? "none" : "auto" }}
        >
          <Sidebar
            filesContent={
              currentAssignment?.project_id ? (
                <FileTree
                  documents={documents}
                  activeDocumentId={currentDocument?.id ?? null}
                  onSelect={(id) => openDocument(id)}
                  projectId={currentAssignment.project_id}
                />
              ) : undefined
            }
            outlineContent={
              <OutlineTree
                content={currentDocument?.content || ""}
                docType={(currentDocument?.doc_type as "latex" | "markdown" | "docx") || "latex"}
                activeLine={cursorLine}
                onNavigate={scrollToLine}
              />
            }
            chatContent={
              assignmentId && currentCourse && currentAssignment ? (
                <ChatPanel
                  assignmentId={assignmentId}
                  courseName={currentCourse.name}
                  assignmentTitle={currentAssignment.title}
                  assignmentType={currentAssignment.assignment_type}
                  documentId={currentDocument?.id}
                  cursorLine={cursorLine}
                  selectionStart={selection?.from}
                  selectionEnd={selection?.to}
                  selectedText={selection?.text}
                  onEdit={handleEdit}
                />
              ) : undefined
            }
            commentsContent={
              <CommentThread
                comments={comments}
                documentId={currentDocument?.id}
                onAddComment={(content, lineStart, lineEnd, parentId) => {
                  const now = new Date().toISOString();
                  addComment({
                    id: crypto.randomUUID(),
                    document_id: currentDocument?.id || "",
                    user_id: "local",
                    content,
                    line_start: lineStart,
                    line_end: lineEnd,
                    parent_id: parentId || null,
                    resolved: false,
                    created_at: now,
                    updated_at: now,
                  });
                }}
                onResolveComment={resolveComment}
                onDeleteComment={deleteComment}
                onReply={handleReply}
                onNavigateToLine={scrollToLine}
              />
            }
            citationsContent={<CitationPanel documentContent={currentDocument?.content} docType={currentDocument?.doc_type} />}
            contextContent={<ContextPanel />}
          />
        </motion.div>

        {/* Editor + Preview */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <SplitPane
            left={
              <div className="relative flex h-full flex-col overflow-hidden">
                <OfflineBanner isOnline={isOnline} isSyncing={isSyncing} pendingCount={pendingCount} />
                <BreadcrumbBar
                  content={currentDocument?.content || ""}
                  docType={currentDocument?.doc_type || "latex"}
                  currentLine={cursorLine}
                  onNavigate={scrollToLine}
                />
                <EditorToolbar />
                <div className="relative flex-1 min-h-0">
                  <LeverEditor
                    value={currentDocument?.content || ""}
                    onChange={handleContentChange}
                    language={currentDocument?.doc_type || "latex"}
                  />
                  <SelectionToolbar onAction={handleSelectionAction} />
                  <CommentPopover onSubmit={handleCommentFromPopover} />
                  <EquationPreview />
                  {!inlineAssistantOpen && (
                    <div className="absolute bottom-3 left-3 right-3 z-30">
                      <AIInlineSuggestion />
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 right-3 z-[35] flex justify-center pointer-events-none">
                    <div className="pointer-events-auto w-full">
                      <InlineAssistant
                        projectId={currentAssignment?.project_id ?? undefined}
                        classContext={currentCourse && currentAssignment ? {
                          courseName: currentCourse.name,
                          dueDate: dueDate ?? undefined,
                          assignmentType: currentAssignment.assignment_type,
                        } : undefined}
                        files={documents.map((d) => ({ id: d.id, filename: d.filename, doc_type: d.doc_type }))}
                        activeFileId={currentDocument?.id}
                      />
                    </div>
                  </div>
                  {pendingEdit && currentDocument && (
                    <DiffOverlay
                      originalContent={currentDocument.content}
                      proposedContent={pendingEdit.proposedContent}
                      description={pendingEdit.description}
                      onAccept={acceptPendingEdit}
                      onReject={rejectPendingEdit}
                    />
                  )}
                </div>
                <StatusBar
                  content={currentDocument?.content || ""}
                  docType={currentDocument?.doc_type || "latex"}
                />
              </div>
            }
            right={
              <div className="h-full overflow-hidden">
                <PreviewPanel onAIFix={assignmentId ? handleAIFix : undefined} />
              </div>
            }
          />

          <motion.div
            className="shrink-0"
            animate={{ opacity: focusMode ? 0.15 : 1 }}
            style={{ pointerEvents: focusMode ? "none" : "auto" }}
          >
            <BottomPanel
              errorsContent={<ErrorList errors={errors} onNavigate={scrollToLine} />}
              logsContent={
                compileResult?.log ? (
                  <pre className="p-3 text-[11px] leading-relaxed font-mono text-[var(--text-secondary)] whitespace-pre-wrap max-h-[400px] overflow-auto">
                    {compileResult.log}
                  </pre>
                ) : (
                  <div className="p-3 text-xs text-[var(--text-muted)]">
                    No compilation logs yet. Compile your document to see output.
                  </div>
                )
              }
              vitalsContent={<VitalsDashboard />}
              versionsContent={<VersionPanel />}
              errorCount={errors.length}
            />
          </motion.div>
        </div>
      </div>
      <EquationPlayground />
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
